import { useCallback, useReducer } from "react";

import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { reset } from "@app/util/undo";

import EditActionType from "../enums/EditActionType";
import ChangeVertexFunction from "../types/ChangeVertexFunction";
import { Paths } from "../types/EditBoundsCommand";

import editBoundsReducer from "./editBoundsReducer";

const useTerritoryBoundsEditor = (paths: Paths) => {
  const analytics = useExtendedAnalytics("Territory bounds", useAnalytics());
  const [state, dispatch] = useReducer(editBoundsReducer, reset(paths));

  const handleChangeVertex: ChangeVertexFunction = useCallback(
    (action, pathIndex, vertexIndex, coordinates) => {
      analytics.using(["Change vertex", action]);
      switch (action) {
        case "add":
          dispatch({
            at: vertexIndex,
            coordinate: [coordinates!.lng, coordinates!.lat],
            pathIndex,
            type: EditActionType.ADD_VERTEX,
          });
          break;
        case "edit":
          dispatch({
            at: vertexIndex,
            coordinate: [coordinates!.lng, coordinates!.lat],
            pathIndex,
            type: EditActionType.EDIT_VERTEX,
          });
          break;
        case "remove":
          dispatch({ at: vertexIndex, pathIndex, type: EditActionType.REMOVE_VERTEX });
          break;
      }
    },
    [analytics]
  );

  return {
    cancel: useCallback(() => dispatch({ type: EditActionType.CANCEL }), []),
    canRedo: state.revertedChanges.size > 0,
    canUndo: state.appliedCommands.size > 0,
    changeVertex: handleChangeVertex,
    hasChanges: state.hasChanges,
    moveTerritory: useCallback(
      (paths: Paths) => {
        dispatch({ paths, type: EditActionType.UPDATE_PATHS });
        analytics.using(["Move whole territory"]);
      },
      [analytics]
    ),
    paths: state.data,
    redo: useCallback(() => {
      dispatch({ type: EditActionType.REDO });
      analytics.using(["Redo"]);
    }, [analytics]),
    reset: useCallback((paths: Paths) => dispatch({ paths, type: EditActionType.RESET }), []),
    undo: useCallback(() => {
      dispatch({ type: EditActionType.UNDO });
      analytics.using(["Undo"]);
    }, [analytics]),
  };
};

export default useTerritoryBoundsEditor;
